import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Axios, { API } from 'src/common/api';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { eDiscoveryUrl } from 'src/common/routes';
import { useGlobalState, setGlobalState } from 'src/state/globalstate';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ImageSearchOutlinedIcon from '@mui/icons-material/ImageSearchOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

export const SIDEBAR_WIDTH = 256;
export const SIDEBAR_COLLAPSED_WIDTH = 78;

interface SidebarProps {
  collapsed: boolean;
  mobileOpen?: boolean;
  onClose?: () => void;
}

const SidebarStyles = () => (
  <style>{`
    .kside{
      position:fixed;top:0;left:0;bottom:0;z-index:30;
      width:${SIDEBAR_WIDTH}px;
      display:flex;flex-direction:column;
      font-family:'Inter',-apple-system,sans-serif;color:#fff;
      background:linear-gradient(185deg,#1b1640 0%,#241b54 55%,#2c2068 100%);
      box-shadow:6px 0 40px -16px rgba(20,12,50,.5);
      transition:width .22s cubic-bezier(.4,0,.2,1);
      overflow-x:hidden;
    }
    .kside.collapsed{width:${SIDEBAR_COLLAPSED_WIDTH}px;}
    .kside::before{
      content:'';position:absolute;top:-120px;left:-80px;width:280px;height:280px;border-radius:50%;
      background:radial-gradient(circle,rgba(171,155,237,.35),transparent 70%);filter:blur(40px);pointer-events:none;
    }
    .kside-top{
      display:flex;align-items:center;
      padding:16px 22px 12px;position:relative;z-index:1;min-height:56px;
      border-bottom:1px solid rgba(255,255,255,.07);
      margin:0 0 4px;
    }
    .kside.collapsed .kside-top{justify-content:center;padding:16px 0 12px;}
    .kside-logo{height:28px;width:auto;}
    .kside-favicon{width:36px;height:36px;border-radius:10px;object-fit:cover;box-shadow:0 6px 16px -6px rgba(108,92,231,.7);}

    .kside-scroll{flex:1 1 auto;overflow-y:auto;overflow-x:hidden;position:relative;z-index:1;}
    .kside-scroll::-webkit-scrollbar{width:0;}

    .kside-section{
      display:flex;align-items:center;justify-content:space-between;gap:8px;width:100%;
      background:transparent;border:none;text-align:left;cursor:pointer;font-family:inherit;
      padding:4px 22px;margin-top:7px;
      font-size:10px;font-weight:600;letter-spacing:1.3px;text-transform:uppercase;
      color:rgba(255,255,255,.38);white-space:nowrap;transition:color .16s ease;
    }
    .kside-section:hover{color:rgba(255,255,255,.62);}
    .kside-chev{font-size:15px !important;flex:none;transition:transform .2s ease;
      color:rgba(255,255,255,.4) !important;fill:currentColor !important;}
    .kside-chev.closed{transform:rotate(-90deg);}
    .kside-sep{height:1px;margin:6px 18px 3px;background:rgba(255,255,255,.1);}
    .kside-nav{display:flex;flex-direction:column;gap:1px;padding:1px 14px;}
    .kside-item{
      display:flex;align-items:center;gap:11px;
      padding:7px 12px;border-radius:10px;cursor:pointer;
      color:rgba(255,255,255,.66);font-size:12.5px;font-weight:500;letter-spacing:.1px;white-space:nowrap;
      border:1px solid transparent;
      transition:background .18s ease,color .18s ease,border-color .18s ease;position:relative;
    }
    .kside.collapsed .kside-item{justify-content:center;padding:9px;}
    .kside-item svg{font-size:18px;flex:none;color:rgba(255,255,255,.46) !important;fill:currentColor !important;transition:color .18s ease;}
    .kside-item:hover{background:rgba(255,255,255,.055);color:#fff;}
    .kside-item:hover svg{color:rgba(255,255,255,.8) !important;}
    .kside-item.active{
      background:rgba(171,155,237,.13);
      color:#fff;border-color:rgba(171,155,237,.2);font-weight:600;
    }
    .kside-item.active svg{color:#ab9bed !important;}
    .kside-item.active::before{
      content:'';position:absolute;left:-14px;top:50%;transform:translateY(-50%);
      width:3px;height:18px;border-radius:0 3px 3px 0;background:#ab9bed;
      box-shadow:0 0 12px rgba(171,155,237,.85);
    }
    .kside.collapsed .kside-label{display:none;}

    .kside-foot{padding:14px 16px 18px;position:relative;z-index:1;}
    .kside-user{
      display:flex;align-items:center;gap:11px;padding:10px;border-radius:14px;
      background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);
    }
    .kside.collapsed .kside-user{justify-content:center;padding:8px;}
    .kside-user-av{width:36px;height:36px;border-radius:50%;flex:none;object-fit:cover;
      background:linear-gradient(135deg,#ab9bed,#6c5ce7);display:grid;place-items:center;
      font-family:'Inter',sans-serif;font-weight:700;font-size:15px;color:#1b1640;}
    .kside-user-info{display:flex;flex-direction:column;line-height:1.25;min-width:0;flex:1;}
    .kside-user-info b{font-size:13px;font-weight:700;color:#fff;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
    .kside-user-info span{font-size:11px;color:rgba(255,255,255,.5);text-transform:capitalize;}
    .kside-logout{
      display:grid;place-items:center;width:32px;height:32px;border-radius:9px;flex:none;
      background:transparent;border:1px solid rgba(255,255,255,.12);color:rgba(255,255,255,.7);cursor:pointer;transition:all .16s;
    }
    .kside-logout:hover{background:rgba(239,68,68,.22);color:#fff;border-color:rgba(239,68,68,.4);}
    .kside-logout svg{font-size:17px;color:inherit !important;fill:currentColor !important;}
    .kside.collapsed .kside-user-info,.kside.collapsed .kside-logout{display:none;}

    /* ---- dark mode: neutral charcoal sidebar instead of purple ---- */
    html.dark .kside{
      background:linear-gradient(185deg,#121319 0%,#15161f 55%,#0e0f14 100%) !important;
      box-shadow:6px 0 40px -16px rgba(0,0,0,.6) !important;
      border-right:1px solid rgba(255,255,255,.06);
    }
    html.dark .kside::before{
      background:radial-gradient(circle,rgba(139,123,240,.18),transparent 70%) !important;
    }
    html.dark .kside-top{ border-bottom-color:rgba(255,255,255,.06) !important; }
    html.dark .kside-item.active{
      background:rgba(139,123,240,.16) !important;border-color:rgba(139,123,240,.28) !important;
    }
    html.dark .kside-item.active svg{ color:#a99bf5 !important; }
    html.dark .kside-item.active::before{ background:#8b7bf0;box-shadow:0 0 12px rgba(139,123,240,.85); }

    /* mobile: slide-in drawer instead of hidden */
    @media (max-width:900px){
      .kside{ transform:translateX(-100%); transition:transform .24s cubic-bezier(.4,0,.2,1); }
      .kside.mobile-open{ transform:translateX(0); box-shadow:8px 0 50px -10px rgba(20,12,50,.6); }
      .kside.collapsed{ width:${SIDEBAR_WIDTH}px; } /* always full width as a drawer */
    }
  `}</style>
);

function Sidebar({ collapsed, mobileOpen, onClose }: SidebarProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [languageArray] = useGlobalState('languageArray');
  const [userRolesList] = useGlobalState('userRoles');

  // The id->role-name map is only loaded by the User Management page otherwise;
  // fetch it here so role-based sidebar gating works on every page.
  useEffect(() => {
    if (userRolesList && userRolesList.length > 0) return;
    (async () => {
      try {
        const res = await Axios.get(API.userRoles);
        setGlobalState('userRoles', res.data);
      } catch (e) {
        // ignore — falls back to least-privilege until roles load
      }
    })();

  }, []);

  // Resolve the logged-in user's role NAME from the id captured at login
  // (localStorage 'userRole'), then map it to a visibility category. Works
  // whether localStorage holds an id ("2") or already a name ("Admin").
  const rawUserRole = localStorage.getItem('userRole') || '';
  const roleName = String(rawUserRole)
    .split(',')
    .map((part) => {
      const key = part.trim();
      const found = (userRolesList || []).find(
        (r: any) => String(r.id) === key
      );
      return found ? String(found.roles) : key;
    })
    .join(' ')
    .toLowerCase();

  const roleCategory = roleName.includes('super')
    ? 'superadmin'
    : roleName.includes('admin')
      ? 'admin'
      : roleName.includes('review')
        ? 'reviewer'
        : roleName.includes('annotat')
          ? 'annotator'
          : 'default';

  // Sidebar visibility rules by role (unknown roles → least privilege):
  //  - Super Admin: everything
  //  - Admin: hide Tag Management + User Management
  //  - Reviewer / Annotator: also hide Login Logs + Activity Logs
  const hiddenNav = new Set<string>();
  if (roleCategory === 'admin') {
    ['Tag Management', 'User Management'].forEach((l) => hiddenNav.add(l));
  } else if (roleCategory !== 'superadmin') {
    ['Tag Management', 'User Management', 'Login Logs', 'Activity Logs'].forEach(
      (l) => hiddenNav.add(l)
    );
  }

  // Section groups are expanded by default; users can collapse each one.
  const [closedSections, setClosedSections] = useState<Record<string, boolean>>(
    {}
  );
  const toggleSection = (title: string) =>
    setClosedSections((s) => ({ ...s, [title]: !s[title] }));

  const t = (i: number, fallback: string) =>
    languageArray.length > 0 && languageArray ? languageArray[i] : fallback;

  const sections = [
    {
      title: 'Tools',
      items: [
        {
          label: t(7, 'Documents'),
          icon: <DescriptionOutlinedIcon />,
          active:
            pathname.includes('/folder') ||
            pathname.includes('/files') ||
            pathname.includes('/views'),
          onClick: () => {
            setGlobalState('ErrorFoundGlobal', true);
            navigate(eDiscoveryUrl.CaseS3 + '/1');
          }
        },
        {
          label: t(10, 'Paste Text'),
          icon: <ContentPasteOutlinedIcon />,
          active: pathname.includes('copyPaste'),
          onClick: () => navigate(eDiscoveryUrl.copyPaste)
        },
        {
          label: t(243, 'Process Image'),
          icon: <ImageOutlinedIcon />,
          active: pathname.includes('/image/'),
          onClick: () => navigate(eDiscoveryUrl.ImagePorcessS3 + '/1')
        },
        {
          label: 'Search Image',
          icon: <ImageSearchOutlinedIcon />,
          active: pathname.includes('search-image'),
          onClick: () => navigate(eDiscoveryUrl.SearchImage)
        },
        // Smart Search hidden from sidebar
        // {
        //   label: 'Smart Search',
        //   icon: <SmartToyOutlinedIcon />,
        //   active: pathname.includes('smart-search'),
        //   onClick: () => navigate(eDiscoveryUrl.Chatbot)
        // },
        {
          label: 'US Letter Search',
          icon: <MailOutlinedIcon />,
          active: pathname.includes('letter-search'),
          onClick: () => navigate(eDiscoveryUrl.letterSearch)
        },
        {
          label: 'IRS',
          icon: <AccountBalanceOutlinedIcon />,
          active: pathname.includes('/irs'),
          onClick: () => navigate(eDiscoveryUrl.SmartSearchNew)
        },
        {
          label: 'Document Annotator',
          icon: <EditOutlinedIcon />,
          active: pathname.includes('annotate'),
          onClick: () => navigate(eDiscoveryUrl.AnnotateDocument + '/1')
        }
      ]
    },
    {
      title: 'Knowledge Base',
      items: [
        {
          label: 'Dictionary',
          icon: <MenuBookOutlinedIcon />,
          active: pathname.includes('/rules'),
          onClick: () => navigate(eDiscoveryUrl.Rules)
        },
        {
          label: 'Editorial Guideline',
          icon: <ArticleOutlinedIcon />,
          active: pathname.includes('editorial-guideline'),
          onClick: () => navigate(eDiscoveryUrl.EditorialClaims)
        }
      ]
    },
    {
      title: 'Administration',
      items: [
        {
          label: 'User Management',
          icon: <GroupOutlinedIcon />,
          active: pathname.includes('user-management'),
          show: !hiddenNav.has('User Management'),
          onClick: () => navigate(eDiscoveryUrl.UserManagement)
        },
        {
          label: 'Login Logs',
          icon: <LoginOutlinedIcon />,
          active: pathname.includes('login-logs'),
          show: !hiddenNav.has('Login Logs'),
          onClick: () => navigate(eDiscoveryUrl.LoginLogs)
        },
        {
          label: 'Change Logs',
          icon: <FactCheckOutlinedIcon />,
          active: pathname.includes('change-logs'),
          onClick: () => navigate(eDiscoveryUrl.changeLogs)
        },
        {
          label: 'Features',
          icon: <WidgetsOutlinedIcon />,
          active: pathname.includes('/features'),
          onClick: () => navigate(eDiscoveryUrl.appFeatures)
        },
        {
          label: 'Activity Logs',
          icon: <HistoryOutlinedIcon />,
          active: pathname.includes('activity-logs'),
          show: !hiddenNav.has('Activity Logs'),
          onClick: () => navigate(eDiscoveryUrl.ActivityLogs)
        },
        {
          label: 'Tag Management',
          icon: <LocalOfferOutlinedIcon />,
          active: pathname.includes('tag-management'),
          show: !hiddenNav.has('Tag Management'),
          onClick: () => navigate(eDiscoveryUrl.TagManagement)
        }
      ]
    }
  ];

  return (
    <>
      <SidebarStyles />
      <aside
        className={
          'kside' +
          (collapsed ? ' collapsed' : '') +
          (mobileOpen ? ' mobile-open' : '')
        }
      >
        <div className="kside-top">
          {collapsed ? (
            <img className="kside-favicon" src="/favicon.ico" alt="Kymera" />
          ) : (
            <img
              className="kside-logo"
              src="/static/images/kymera-logo-light.svg"
              alt="Kymera"
            />
          )}
        </div>

        <div className="kside-scroll">
          {sections.map((section, si) => {
            const visible = section.items.filter(
              (it) => (it as any).show !== false
            );
            if (visible.length === 0) return null;
            const isClosed = !collapsed && !!closedSections[section.title];
            return (
              <div key={section.title}>
                {collapsed ? (
                  si > 0 && <div className="kside-sep" />
                ) : (
                  <button
                    type="button"
                    className="kside-section"
                    onClick={() => toggleSection(section.title)}
                    aria-expanded={!isClosed}
                  >
                    <span>{section.title}</span>
                    <ExpandMoreRoundedIcon
                      className={'kside-chev' + (isClosed ? ' closed' : '')}
                    />
                  </button>
                )}
                {!isClosed && (
                  <nav className="kside-nav">
                    {visible.map((it) => (
                      <div
                        key={it.label}
                        className={'kside-item' + (it.active ? ' active' : '')}
                        data-nav={String(it.label)
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, '-')
                          .replace(/^-|-$/g, '')}
                        onClick={() => {
                          it.onClick();
                          if (onClose) onClose();
                        }}
                        title={collapsed ? it.label : undefined}
                      >
                        {it.icon}
                        <span className="kside-label">{it.label}</span>
                      </div>
                    ))}
                  </nav>
                )}
              </div>
            );
          })}
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
